/**
 * Wall-clock helpers on the browser's local time. `Date.prototype.toISOString()`
 * reports UTC, which in a zone ahead of UTC still says "yesterday" between local
 * midnight and the offset (00:00–01:00 in UTC+1).
 */

const pad2 = (value: number): string => String(value).padStart(2, '0');

/** Local civil date, `YYYY-MM-DD`. */
export function toLocalDateString(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** Local wall-clock time, `HH:mm`. */
export function toLocalTimeString(date: Date): string {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** Local wall-clock date-time, `YYYY-MM-DDTHH:mm`, with no offset. */
export function toLocalDateTimeString(date: Date): string {
  return `${toLocalDateString(date)}T${toLocalTimeString(date)}`;
}

/** `days` calendar days later at the same local time — not 24-hour steps, so a DST change cannot skip or repeat a date. */
export function addLocalDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days, date.getHours(), date.getMinutes());
}

/** A civil date (`YYYY-MM-DD`) as a local-midnight Date, so calendars built on local Dates show that very day. */
export function localDateOf(civilDate: string): Date {
  const [year, month, day] = civilDate.split('-').map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
}

/** The instant a local civil day (`YYYY-MM-DD`) starts, as an ISO string. */
export function localDayStartIso(civilDate: string): string {
  const [year, month, day] = civilDate.split('-').map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1).toISOString();
}

/** The last millisecond of a local civil day (`YYYY-MM-DD`), as an ISO string: an upper bound that includes the whole day. */
export function localDayEndIso(civilDate: string): string {
  const [year, month, day] = civilDate.split('-').map(Number);
  return new Date(new Date(year ?? 0, (month ?? 1) - 1, (day ?? 1) + 1).getTime() - 1).toISOString();
}
